import React, { useState, useEffect, useMemo } from 'react';
import {
  Container,
  Card,
  Row,
  Col,
  Table,
  Button,
  Form,
  Alert,
  Badge,
  Spinner,
  InputGroup,
  ProgressBar,
  Modal
} from 'react-bootstrap';
import {
  ClipboardList,
  Search,
  Save,
  RefreshCw,
  AlertTriangle,
  CheckCircle,
  XCircle,
  ArrowLeft,
  TrendingUp,
  TrendingDown,
  Download,
} from 'lucide-react';
import * as XLSX from 'xlsx';
import stockService from '../../services/stockService';

// Formatage devise local (même convention FCFA que le reste de l'app)
const formatCurrency = (amount) => {
  if (amount === null || amount === undefined || isNaN(amount)) return '0 FCFA';
  return new Intl.NumberFormat('fr-FR').format(Math.round(Number(amount))) + ' FCFA';
};


// 👇 AJOUTER
// Valeur unitaire : coût d'achat en priorité, repli sur le prix de vente.
// Gère aussi les noms snake_case renvoyés par /manager/stock (unit_cost / sell_price).
const getUnitValue = (item) => {
  const cost = item.unitCost ?? item.unit_cost ?? item.costPrice ?? 0;
  if (cost && cost > 0) return cost;
  return item.sellPrice ?? item.sell_price ?? item.price ?? 0;
};

const InventoryMode = ({ onClose }) => {
  // ===== États (tous les hooks AVANT tout return) =====
  const [items, setItems] = useState([]);          // stock chargé (quantité théorique)
  const [counts, setCounts] = useState({});         // { [productId]: { counted, reason } }
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filtres
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');

  // Soumission
  const [submitting, setSubmitting] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [showConfirm, setShowConfirm] = useState(false);
  const [results, setResults] = useState(null);     // { success: [], failed: [] }
  const [showResults, setShowResults] = useState(false);

  // ===== Chargement initial =====
  useEffect(() => {
    loadStock();
  }, []);

  const loadStock = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await stockService.getStock();
      if (response.success) {
        const sorted = [...(response.data || [])].sort((a, b) =>
          (a.productName || '').localeCompare(b.productName || '')
        );
        setItems(sorted);
      } else {
        setError(response.message || 'Erreur lors du chargement du stock');
      }
    } catch (e) {
      console.error('❌ Erreur chargement inventaire:', e);
      setError('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  // ===== Helpers de saisie =====
  const getCount = (productId) => counts[productId] || { counted: '', reason: '' };

  const handleCountChange = (productId, value) => {
    // On accepte vide ou entier >= 0
    if (value !== '' && (isNaN(value) || parseInt(value, 10) < 0)) return;
    setCounts((prev) => ({
      ...prev,
      [productId]: { ...getCount(productId), counted: value }
    }));
  };

  const handleReasonChange = (productId, value) => {
    setCounts((prev) => ({
      ...prev,
      [productId]: { ...getCount(productId), reason: value }
    }));
  };

  // ===== Catégories pour le filtre =====
  const categories = useMemo(() => {
    const set = new Set(items.map((i) => i.categoryName).filter(Boolean));
    return Array.from(set).sort();
  }, [items]);

  // ===== Filtrage (affichage uniquement) =====
  const filteredItems = useMemo(() => {
    return items.filter((item) => {
      const matchSearch =
        !searchTerm ||
        (item.productName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (item.productSku || '').toLowerCase().includes(searchTerm.toLowerCase());
      const matchCat = !categoryFilter || item.categoryName === categoryFilter;
      return matchSearch && matchCat;
    });
  }, [items, searchTerm, categoryFilter]);

  // ===== Lignes avec écart (calculées sur TOUS les items, pas seulement filtrés) =====
  const ecartLines = useMemo(() => {
    return items
      .map((item) => {
        const c = counts[item.productId];
        if (!c || c.counted === '' || c.counted === null || c.counted === undefined) return null;
        const counted = parseInt(c.counted, 10);
        if (isNaN(counted)) return null;
        const theorique = item.quantity ?? item.currentStock ?? 0;
        const ecart = counted - theorique;
        if (ecart === 0) return null;
        return {
          item,
          counted,
          ecart,
          reason: (c.reason || '').trim(),
         valeur: ecart * getUnitValue(item)
        };
      })
      .filter(Boolean);
  }, [items, counts]);

  // ===== Récapitulatif =====
  const summary = useMemo(() => {
    const countedTotal = Object.values(counts).filter(
      (c) => c.counted !== '' && c.counted !== null && c.counted !== undefined && !isNaN(parseInt(c.counted, 10))
    ).length;
    const manques = ecartLines.filter((l) => l.ecart < 0);
    const surplus = ecartLines.filter((l) => l.ecart > 0);
    const valeurManque = manques.reduce((s, l) => s + l.valeur, 0); // négatif
    const valeurSurplus = surplus.reduce((s, l) => s + l.valeur, 0); // positif
    return {
      countedTotal,
      nbEcarts: ecartLines.length,
      nbManques: manques.length,
      nbSurplus: surplus.length,
      valeurManque,
      valeurSurplus,
      valeurNette: valeurManque + valeurSurplus
    };
  }, [counts, ecartLines]);

  // Justifications manquantes (écart != 0 sans raison)
  const missingReasons = useMemo(
    () => ecartLines.filter((l) => !l.reason),
    [ecartLines]
  );

  // ===== Soumission séquentielle =====
  const runSubmission = async (lines) => {
    setSubmitting(true);
    setProgress({ current: 0, total: lines.length });

    const success = [];
    const failed = [];
    const dateStr = new Date().toLocaleDateString('fr-FR');

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      const reason = `Inventaire ${dateStr}: ${line.reason}`;

      const res = await stockService.adjustStockForInventory(
        line.item.productId,
        line.counted,
        reason
      );

      if (res && res.success) {
        success.push(line);
        // Mise à jour locale : le théorique devient le compté, on vide la ligne
        setItems((prev) =>
          prev.map((it) =>
            it.productId === line.item.productId ? { ...it, quantity: line.counted } : it
          )
        );
        setCounts((prev) => {
          const next = { ...prev };
          delete next[line.item.productId];
          return next;
        });
      } else {
        failed.push({ ...line, error: (res && res.message) || 'Échec inconnu' });
      }

      setProgress({ current: i + 1, total: lines.length });
    }

    setResults({ success, failed });
    setSubmitting(false);
    setShowResults(true);
  };

  const handleValidate = () => {
    if (missingReasons.length > 0) {
      setError(
        `Veuillez justifier les ${missingReasons.length} écart(s) avant de valider (le champ "Justification" est obligatoire dès qu'il y a un écart).`
      );
      return;
    }
    setError(null);
    setShowConfirm(true);
  };

  const handleConfirm = () => {
    setShowConfirm(false);
    runSubmission(ecartLines);
  };

  const handleRetryFailed = () => {
    if (!results || results.failed.length === 0) return;
    const toRetry = results.failed.map((f) => ({
      item: f.item,
      counted: f.counted,
      ecart: f.ecart,
      reason: f.reason,
      valeur: f.valeur
    }));
    setShowResults(false);
    runSubmission(toRetry);
  };

  // ===== Export Excel =====
  const handleExport = () => {
    const rows = items.map((item) => {
      const c = counts[item.productId] || {};
      const theorique = item.quantity ?? item.currentStock ?? 0;
      const hasCount =
        c.counted !== '' && c.counted !== null && c.counted !== undefined &&
        !isNaN(parseInt(c.counted, 10));
      const counted = hasCount ? parseInt(c.counted, 10) : null;
      const ecart = counted !== null ? counted - theorique : null;
      const cost = getUnitValue(item);
      return {
        'Produit': item.productName || '',
        'SKU': item.productSku || '',
        'Catégorie': item.categoryName || '',
        'Stock théorique': theorique,
        'Stock physique': counted !== null ? counted : '',
        'Écart': ecart !== null ? ecart : '',
        'Valeur écart (FCFA)': ecart !== null ? Math.round(ecart * cost) : '',
        'Justification': c.reason || ''
      };
    });

    const ws = XLSX.utils.json_to_sheet(rows);
    ws['!cols'] = [
      { wch: 30 }, { wch: 14 }, { wch: 18 }, { wch: 14 },
      { wch: 14 }, { wch: 8 }, { wch: 18 }, { wch: 40 }
    ];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Inventaire');
    const dateStr = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(wb, `inventaire_${dateStr}.xlsx`);
  };

  // ===== Rendu d'une ligne =====
  const renderRow = (item) => {
    const c = getCount(item.productId);
    const theorique = item.quantity ?? item.currentStock ?? 0;
    const compte = c.counted;
    const hasCount = compte !== '' && compte !== null && compte !== undefined;
    const ecart = hasCount ? parseInt(compte, 10) - theorique : null;
    const valeurEcart = ecart !== null ? ecart * getUnitValue(item) : null;
    let ecartBadge = null;
    if (ecart === null) {
      ecartBadge = <span className="text-muted">—</span>;
    } else if (ecart === 0) {
      ecartBadge = <Badge bg="success">Conforme</Badge>;
    } else if (ecart > 0) {
      ecartBadge = (
        <Badge bg="info" className="d-inline-flex align-items-center gap-1">
          <TrendingUp size={12} /> +{ecart}
        </Badge>
      );
    } else {
      ecartBadge = (
        <Badge bg="danger" className="d-inline-flex align-items-center gap-1">
          <TrendingDown size={12} /> {ecart}
        </Badge>
      );
    }

    return (
      <tr key={item.productId}>
        <td>
          <div className="fw-semibold">{item.productName}</div>
          {item.productSku && <small className="text-muted">{item.productSku}</small>}
        </td>
        <td>
          <small className="text-muted">{item.categoryName || 'Non catégorisé'}</small>
        </td>
        <td className="text-center fw-semibold">{theorique}</td>
        <td style={{ minWidth: 110 }}>
          <Form.Control
            type="number"
            onWheel={e => e.target.blur()} // Empêche le scroll de changer la valeur
            min="0"
            placeholder="Compté"
            value={compte}
            onChange={(e) => handleCountChange(item.productId, e.target.value)}
            size="sm"
          />
        </td>
        <td className="text-center">{ecartBadge}</td>
        <td className="text-end">
          {valeurEcart !== null && ecart !== 0 ? (
            <span className={ecart < 0 ? 'text-danger' : 'text-info'}>
              {formatCurrency(valeurEcart)}
            </span>
          ) : (
            <span className="text-muted">—</span>
          )}
        </td>
        <td style={{ minWidth: 180 }}>
          {ecart !== null && ecart !== 0 ? (
            <Form.Control
              type="text"
              placeholder="Raison de l'écart (obligatoire)"
              value={c.reason}
              onChange={(e) => handleReasonChange(item.productId, e.target.value)}
              size="sm"
              isInvalid={!c.reason?.trim()}
            />
          ) : (
            <span className="text-muted">—</span>
          )}
        </td>
      </tr>
    );
  };

  // ===== Rendus conditionnels (APRÈS tous les hooks) =====
  if (loading) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3 text-muted">Chargement du stock…</p>
      </Container>
    );
  }

  return (
    <Container fluid className="py-3">
      {/* En-tête */}
      <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 gap-2">
        <div className="d-flex align-items-center gap-2">
          {onClose && (
            <Button variant="outline-secondary" size="sm" onClick={onClose}>
              <ArrowLeft size={16} className="me-1" /> Retour
            </Button>
          )}
          <h4 className="mb-0 d-flex align-items-center gap-2">
            <ClipboardList size={22} /> Inventaire physique
          </h4>
        </div>
        <div className="d-flex gap-2">
          <Button variant="outline-success" size="sm" onClick={handleExport} disabled={submitting}>
            <Download size={16} className="me-1" /> Exporter
          </Button>
          <Button variant="outline-primary" size="sm" onClick={loadStock} disabled={submitting}>
            <RefreshCw size={16} className="me-1" /> Recharger
          </Button>
        </div>
      </div>

      {error && (
        <Alert variant="danger" onClose={() => setError(null)} dismissible>
          <AlertTriangle size={16} className="me-1" />
          {error}
        </Alert>
      )}

      {/* Barre de progression pendant la soumission */}
      {submitting && (
        <Card className="mb-3 border-primary">
          <Card.Body>
            <div className="d-flex justify-content-between mb-2">
              <span className="fw-semibold">Enregistrement des ajustements…</span>
              <span>
                {progress.current} / {progress.total}
              </span>
            </div>
            <ProgressBar
              now={progress.total ? (progress.current / progress.total) * 100 : 0}
              animated
            />
            <small className="text-muted">
              Ne fermez pas la page tant que l'enregistrement n'est pas terminé.
            </small>
          </Card.Body>
        </Card>
      )}

      {/* Récapitulatif */}
      <Row className="g-2 mb-3">
        <Col xs={6} md={3}>
          <Card className="text-center h-100">
            <Card.Body className="py-2">
              <div className="h5 mb-0">{summary.countedTotal}</div>
              <small className="text-muted">Produits comptés</small>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={6} md={3}>
          <Card className="text-center h-100">
            <Card.Body className="py-2">
              <div className="h5 mb-0 text-danger">{summary.nbManques}</div>
              <small className="text-muted">Manques</small>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={6} md={3}>
          <Card className="text-center h-100">
            <Card.Body className="py-2">
              <div className="h5 mb-0 text-info">{summary.nbSurplus}</div>
              <small className="text-muted">Surplus</small>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={6} md={3}>
          <Card className="text-center h-100">
            <Card.Body className="py-2">
              <div
                className={`h6 mb-0 ${summary.valeurNette < 0 ? 'text-danger' : 'text-success'}`}
              >
                {formatCurrency(summary.valeurNette)}
              </div>
              <small className="text-muted">Valeur nette écart</small>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Filtres */}
      <Card className="mb-3">
        <Card.Body className="py-2">
          <Row className="g-2 align-items-center">
            <Col md={6}>
              <InputGroup size="sm">
                <InputGroup.Text>
                  <Search size={16} />
                </InputGroup.Text>
                <Form.Control
                  placeholder="Rechercher un produit (nom ou SKU)…"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </InputGroup>
            </Col>
            <Col md={4}>
              <Form.Select
                size="sm"
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
              >
                <option value="">Toutes les catégories</option>
                {categories.map((cat) => (
                  <option key={cat} value={cat}>
                    {cat}
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col md={2} className="text-md-end">
              <small className="text-muted">{filteredItems.length} produit(s)</small>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Tableau d'inventaire */}
      <Card>
        <Card.Body className="p-0">
          <Table responsive hover className="mb-0 align-middle">
            <thead className="table-light">
              <tr>
                <th>Produit</th>
                <th>Catégorie</th>
                <th className="text-center">Théorique</th>
                <th>Physique</th>
                <th className="text-center">Écart</th>
                <th className="text-end">Valeur écart</th>
                <th>Justification</th>
              </tr>
            </thead>
            <tbody>
              {filteredItems.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center text-muted py-4">
                    Aucun produit à afficher.
                  </td>
                </tr>
              ) : (
                filteredItems.map((item) => renderRow(item))
              )}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      {/* Barre d'action */}
      <div className="d-flex justify-content-between align-items-center mt-3 flex-wrap gap-2">
        <div>
          {summary.nbEcarts > 0 ? (
            <span className="text-muted">
              {summary.nbEcarts} écart(s) à enregistrer
              {missingReasons.length > 0 && (
                <span className="text-danger ms-2">
                  ({missingReasons.length} sans justification)
                </span>
              )}
            </span>
          ) : (
            <span className="text-muted">Aucun écart détecté pour l'instant.</span>
          )}
        </div>
        <Button
          variant="primary"
          onClick={handleValidate}
          disabled={submitting || summary.nbEcarts === 0}
        >
          <Save size={16} className="me-1" /> Valider l'inventaire
        </Button>
      </div>

      {/* Modal de confirmation */}
      <Modal show={showConfirm} onHide={() => setShowConfirm(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirmer l'inventaire</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Vous allez ajuster le stock de <strong>{summary.nbEcarts} produit(s)</strong> :</p>
          <ul className="mb-3">
            <li className="text-danger">{summary.nbManques} manque(s) — {formatCurrency(summary.valeurManque)}</li>
            <li className="text-info">{summary.nbSurplus} surplus — {formatCurrency(summary.valeurSurplus)}</li>
            <li className="fw-semibold">
              Valeur nette : {formatCurrency(summary.valeurNette)}
            </li>
          </ul>
          <Alert variant="warning" className="mb-0 py-2">
            <small>
              Chaque écart sera enregistré comme un ajustement de stock daté et justifié.
              Cette action est définitive.
            </small>
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setShowConfirm(false)}>
            Annuler
          </Button>
          <Button variant="primary" onClick={handleConfirm}>
            Confirmer et enregistrer
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal de résultats */}
      <Modal show={showResults} onHide={() => setShowResults(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Résultat de l'inventaire</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {results && (
            <>
              <div className="d-flex align-items-center gap-2 mb-2 text-success">
                <CheckCircle size={18} />
                <span>{results.success.length} ajustement(s) enregistré(s)</span>
              </div>
              {results.failed.length > 0 && (
                <>
                  <div className="d-flex align-items-center gap-2 mb-2 text-danger">
                    <XCircle size={18} />
                    <span>{results.failed.length} échec(s)</span>
                  </div>
                  <ul className="small text-muted">
                    {results.failed.map((f) => (
                      <li key={f.item.productId}>
                        <strong>{f.item.productName}</strong> — {f.error}
                      </li>
                    ))}
                  </ul>
                  <Alert variant="info" className="py-2 mb-0">
                    <small>
                      Les produits échoués conservent leur saisie. Vous pouvez relancer
                      uniquement ces produits.
                    </small>
                  </Alert>
                </>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          {results && results.failed.length > 0 && (
            <Button variant="warning" onClick={handleRetryFailed}>
              <RefreshCw size={16} className="me-1" /> Relancer les échecs
            </Button>
          )}
          <Button variant="primary" onClick={() => setShowResults(false)}>
            Fermer
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default InventoryMode;